import React, { useState, useEffect, useRef } from "react";
import Image from "../../../components/AppImage";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useNavigate } from "react-router-dom";
import { Calendar, Users, CheckCircle, X } from 'lucide-react';
import AnnouncementWidget from './AnnouncementWidget';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [permitDate, setPermitDate] = useState('');
  const [visitorCount, setVisitorCount] = useState(1);
  const [isPermitModalOpen, setIsPermitModalOpen] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  const heroSlides = [
  {
    id: 1,
    image: "/assets/images/ZZ_Adventure_Caving-1769668265029.JPG",
    imageAlt: "Adventurous kayakers paddling through the mystical underground river cave system with dramatic stalactites and stalagmites illuminated by headlamps, showcasing the legendary 8.2-kilometer subterranean journey through ancient limestone chambers",
    title: "Adventure Caving: Underground River Kayaking",
    subtitle: "Paddle Through Earth\'s Hidden Cathedral • 8.2km Subterranean Journey • Ancient Limestone Formations",
    cta: "Explore the Underground River",
    description: "Embark on an unforgettable kayaking adventure through one of the world's most spectacular underground river systems. Glide silently through cathedral-like chambers where millions of years of geological artistry have created a subterranean masterpiece of stalactites, stalagmites, and otherworldly rock formations. This 8.2-kilometer navigable underground river flows directly from mountain heart to sea, offering adventurers a rare glimpse into Earth's hidden wonders. As your kayak drifts through the darkness, illuminated only by headlamps revealing nature's sculptural genius, you'll witness the same pristine beauty that earned PPSRNP its place as a UNESCO World Heritage Site and one of the New 7 Wonders of Nature."
  },
  {
    id: 2,
    image: "/assets/images/Araw_ng_Taraw-1769665467123.jpg",
    imageAlt: "Aerial view of Sabang wharf facilities during Araw ng Taraw cultural festival celebration, showcasing organized community infrastructure with boats, visitors, and coastal development supporting sustainable tourism in PPSRNP",
    title: "Araw ng Taraw: Celebrating Community & Conservation",
    subtitle: "Annual Cultural Festival • Community Pride • Sustainable Tourism Infrastructure",
    cta: "Discover Community Celebrations",
    description: "Experience the vibrant spirit of Puerto Princesa Subterranean River National Park during Araw ng Taraw, the annual cultural festival that celebrates the deep connection between local communities and their natural heritage. This aerial view of Sabang wharf facilities captures the organized infrastructure that supports sustainable tourism while maintaining ecological integrity. The festival brings together thousands of community members, conservation partners, and visitors to honor the collaborative stewardship that has made PPSRNP a global conservation success story. From traditional performances to environmental education programs, Araw ng Taraw demonstrates how cultural pride and conservation awareness create lasting protection for this UNESCO World Heritage treasure."
  },
  {
    id: 3,
    image: "/assets/images/UNDERGROUND_-_6-1769151502955.jpg",
    imageAlt: "Breathtaking sunset over West Philippine Sea with dramatic limestone karst formations silhouetted against golden sky, showcasing the romantic scenic beauty of PPSRNP coastline",
    title: "Golden Hour Magic: Where Limestone Meets the Sea",
    subtitle: "Witness Nature\'s Daily Masterpiece • Coastal Paradise • UNESCO World Heritage Beauty",
    cta: "Experience the Magic"
  },
  {
    id: 4,
    image: "/assets/images/A038A259-FC75-4E82-B1CE-DBFAF08EF619-1765250829061.png",
    imageAlt: "Stunning landscape photo of Puerto Princesa Subterranean River featuring crystal clear turquoise water, dramatic limestone karst formations with caves, tourist boat with visitors, lush green vegetation hanging from cliff faces, perfect representation of the mountain-to-sea theme and underground river experience",
    title: "Experience Nature\'s Masterpiece",
    subtitle: "From Mountain Peaks to Underground Rivers • A Complete Ecological Journey",
    cta: "Discover More"
  },
  {
    id: 5,
    image: "https://images.unsplash.com/photo-1723419728111-82b886ed793d",
    imageAlt: "Breathtaking view of the iconic Puerto Princesa Underground River entrance perfectly framed by pristine tropical vegetation with visitors in boats embarking on their legendary cave adventure through turquoise waters",
    title: "Portal to Wonder: The Legendary Underground River Beckons",
    subtitle: "Gateway to Earth\'s Hidden Cathedral • Crystal Waters • Ancient Limestone Majesty",
    cta: "Begin Your Adventure",
    description: "Step into paradise and witness one of the world's most extraordinary natural phenomena—the entrance to Puerto Princesa Subterranean River National Park's legendary underground river system. Framed by pristine tropical vegetation and crystal-clear turquoise waters, this mystical portal invites adventurers into an 8.2-kilometer journey through Earth's hidden cathedral. As your boat glides toward the dramatic limestone cave entrance, you're following in the footsteps of millions who have experienced this UNESCO World Heritage wonder that flows directly from mountain heart to sea. The ancient pandanus trees standing sentinel create a natural frame for this geological masterpiece, where millions of years of water and time have carved chambers of unparalleled beauty."
  }];

  // Scrolling recognition text
  const recognitionTexts = [
  "UNESCO World Heritage Site",
  "New 7 Wonders of Nature",
  "UNESCO World Heritage Convention",
  "Ramsar Convention"];


  // Initialize button position to middle-right instead of center
  useEffect(() => {
    const positionButtonMiddleRight = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      setButtonPosition({
        x: viewportWidth - 84, // 84px from right edge (56px button + 28px margin)
        y: viewportHeight / 2 - 28 // Vertically centered (28 is half of button height)
      });
    };

    if (buttonPosition?.x === null) {
      positionButtonMiddleRight();
    }

    window.addEventListener('resize', positionButtonMiddleRight);
    return () => window.removeEventListener('resize', positionButtonMiddleRight);
  }, [buttonPosition?.x]);

  // Handle mouse down for drag start
  const handleMouseDown = (e) => {
    if (e?.target?.closest('button')) {
      setIsDragging(true);
      const rect = buttonRef?.current?.getBoundingClientRect();
      setDragOffset({
        x: e?.clientX - rect?.left,
        y: e?.clientY - rect?.top
      });
    }
  };

  // Handle mouse move for dragging
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging && buttonRef?.current) {
        const newX = e?.clientX - dragOffset?.x;
        const newY = e?.clientY - dragOffset?.y;

        // Constrain to viewport boundaries
        const maxX = window.innerWidth - 56; // button width
        const maxY = window.innerHeight - 56; // button height

        setButtonPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides?.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const handleCTAClick = () => {
    if (currentSlide === 0) navigate("/community-engagement-platform");else
    if (currentSlide === 1) navigate("/interactive-conservation-map");else
    if (currentSlide === 2) navigate("/visitor-planning-hub");else
    if (currentSlide === 3) navigate("/biodiversity-status-center");else
    navigate("/site-showcase-gallery");
  };

  const handleCheckPermit = (e) => {
    e?.preventDefault();
    navigate(`/visitor-planning-hub?date=${permitDate}&visitors=${visitorCount}`);
  };

  return (
    <section className="relative h-screen overflow-hidden">
      {/* Scrolling Recognition Strip */}
      <div className="absolute top-[96px] lg:top-[112px] left-0 right-0 z-20 bg-[var(--color-primary)] dark:bg-[var(--color-secondary)] py-2 lg:py-3 overflow-hidden">
        <div className="flex whitespace-nowrap animate-scroll-left">
          {/* Duplicate the array twice for seamless loop */}
          {[...recognitionTexts, ...recognitionTexts]?.map((text, index) =>
          <div key={index} className="inline-flex items-center mx-8">
              <span className="text-white font-semibold text-sm lg:text-lg">{text}</span>
              <span className="mx-8 text-white/50">•</span>
            </div>
          )}
        </div>
      </div>
      {/* Background Slides - Increased brightness */}
      {heroSlides?.map((slide, index) =>
      <div
        key={slide?.id}
        className={`absolute inset-0 transition-opacity duration-1000 ${
        index === currentSlide ? "opacity-100" : "opacity-0"}`
        }>

          <Image
          src={slide?.image}
          alt={slide?.imageAlt}
          className="w-full h-full object-cover cinemagraph-scale brightness-110" />

          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/60"></div>
        </div>
      )}
      {/* Announcement Widget - NEW */}
      <AnnouncementWidget />
      {/* Draggable Floating Permit Icon Button - HIDDEN */}
      {/* Uncomment below to restore permit availability widget
        <button
         ref={buttonRef}
         onClick={(e) => {
           if (!isDragging) {
             setIsPermitModalOpen(true);
           }
         }}
         onMouseDown={handleMouseDown}
         style={{
           left: buttonPosition?.x !== null ? `${buttonPosition?.x}px` : '50%',
           top: buttonPosition?.y !== null ? `${buttonPosition?.y}px` : '50%',
           transform: buttonPosition?.x === null ? 'translate(-50%, -50%)' : 'none',
           cursor: isDragging ? 'grabbing' : 'grab'
         }}
         className="fixed z-30 w-14 h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 group"
         aria-label="Check Permit Availability">
         <CheckCircle className="h-7 w-7" />
         <span className="absolute right-16 bg-gray-900/90 text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
           Check Permit
         </span>
        </button>
        */}
      {/* Permit Modal - HIDDEN */}
      {/* Uncomment below to restore permit modal
        {isPermitModalOpen &&
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
           <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 m-4">
             <button
             onClick={() => setIsPermitModalOpen(false)}
             className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
             aria-label="Close modal">
                <X className="h-6 w-6" />
             </button>
              <div className="flex items-center space-x-2 mb-4">
               <CheckCircle className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
               <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                 Check Permit Availability
               </h3>
             </div>
             
             <form onSubmit={handleCheckPermit} className="space-y-4">
               <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Visit Date
                 </label>
                 <div className="relative">
                   <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                   <input
                   type="date"
                   value={permitDate}
                   onChange={(e) => setPermitDate(e?.target?.value)}
                   min={new Date()?.toISOString()?.split('T')?.[0]}
                   required
                   className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-emerald-500 transition-colors" />
                  </div>
               </div>
                <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Number of Visitors
                 </label>
                 <div className="relative">
                   <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                   <input
                   type="number"
                   value={visitorCount}
                   onChange={(e) => setVisitorCount(e?.target?.value)}
                   min="1"
                   max="50"
                   required
                   className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-emerald-500 transition-colors" />
                  </div>
               </div>
                <button
               type="submit"
               className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg font-semibold transition-colors shadow-lg">
                  Check Availability
               </button>
             </form>
           </div>
         </div>
        }
        */






      }
      {/* Content Overlay */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-4 lg:px-8 mt-40">
          <div className="max-w-4xl">
            {/* Main Title - Colorful fonts */}
            <h1 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight">
              <span className="text-white drop-shadow-2xl">
                {heroSlides?.[currentSlide]?.title}
              </span>
            </h1>

            {/* Subtitle - Enhanced visibility */}
            <p className="text-xl lg:text-2xl text-emerald-100 drop-shadow-lg mb-8 max-w-2xl font-medium">
              {heroSlides?.[currentSlide]?.subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                size="lg"
                iconName="ArrowRight"
                iconPosition="right"
                onClick={handleCTAClick}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-2xl border-2 border-white/30">

                {heroSlides?.[currentSlide]?.cta}
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Play"
                iconPosition="left"
                onClick={() => navigate("/site-showcase-gallery")}
                className="bg-white hover:bg-gray-50 text-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-700 dark:text-white font-bold shadow-2xl border-2 border-emerald-600">

                Watch Virtual Tour
              </Button>
            </div>

            {/* Live Stats */}
            <div className="grid grid-cols-3 gap-6 mt-12 max-w-2xl">
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-3xl lg:text-4xl font-bold text-yellow-300 mb-1 drop-shadow-lg">
                  8.2km
                </div>
                <div className="text-sm text-gray-200 font-medium">
                  Underground River
                </div>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-3xl lg:text-4xl font-bold text-yellow-300 mb-1 drop-shadow-lg">
                  800+
                </div>
                <div className="text-sm text-gray-200 font-medium">
                  Species Protected
                </div>
              </div>
              <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-3xl lg:text-4xl font-bold text-yellow-300 mb-1 drop-shadow-lg">
                  5,753
                </div>
                <div className="text-sm text-gray-200 font-medium">
                  Hectares Preserved
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-3">
        {heroSlides?.map((_, index) =>
        <button
          key={index}
          onClick={() => setCurrentSlide(index)}
          className={`w-12 h-1 rounded-full transition-all ${
          index === currentSlide ?
          "bg-[var(--color-accent)] w-16" :
          "bg-white/30"}`
          } />

        )}
      </div>
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 right-8 z-20 animate-bounce">
        <Icon name="ChevronDown" size={32} color="var(--color-foreground)" />
      </div>
    </section>);

};

export default HeroSection;